import { Avatar, Table, Pagination } from "flowbite-react";
import { Link, useSearchParams } from "react-router-dom";
import { ChurnPageFilters } from "./ChurnPageFilters";
import { formatLifespan, getFormattedMoney } from "../../utils";
import { ScrollableTableWrapper, PaginationInfo } from "../../components";
import { useEmptyStateTracking } from "../../hooks/useEmptyStateTracking";

const columns = [
  "Account Name",
  "CSM",
  "Contract End Date",
  "MRR",
  "Churn Reason",
  "Customer Lifespan",
  "CLTV",
  "Segment",
  "Country",
];

const SkeletonRow = () => (
  <Table.Row className="animate-pulse">
    <Table.Cell className="sticky left-0 z-10 bg-white shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
      <div className="flex items-center space-x-3">
        <div className="h-8 w-8 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
      </div>
    </Table.Cell>
    {Array.from({ length: columns.length - 1 }).map((_, index) => (
      <Table.Cell key={index}>
        <div className="h-4 w-20 rounded bg-gray-200 dark:bg-gray-700"></div>
      </Table.Cell>
    ))}
  </Table.Row>
);

export const ChurnPageTable = ({
  churnedAccounts,
  dateRange,
  pagination,
  onPageChange,
  isLoading,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const hasData = churnedAccounts && churnedAccounts.length > 0;

  // Analysis-only surface (no alarm): empty churn is normal for a healthy
  // team — date_range discriminates filtered-empty from broken-empty.
  useEmptyStateTracking("churn_table", {
    isEmpty: !hasData,
    isLoading: !!isLoading,
    properties: { date_range: dateRange ?? null },
  });
  const { page, totalPages, total, pageSize } = pagination || {};

  return (
    <div className="mx-5 mb-5 flex flex-col items-start justify-start gap-2.5 rounded-lg bg-white pb-4 shadow">
      <ChurnPageFilters
        dateRange={dateRange}
        selectedTimeRange={searchParams.get("range")}
        onTimeRangeChange={(range) =>
          setSearchParams((searchParams) => {
            if (range) {
              searchParams.set("range", range);
            } else {
              searchParams.delete("range");
            }
            return searchParams;
          })
        }
      />

      {isLoading ? (
        <ScrollableTableWrapper height="calc(100vh - 320px)">
          <Table striped className="border-b drop-shadow-none">
            <Table.Head>
              {columns.map((column, index) => (
                <Table.HeadCell
                  key={column}
                  className={
                    index === 0
                      ? "sticky left-0 z-20 bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                      : ""
                  }
                >
                  {column}
                </Table.HeadCell>
              ))}
            </Table.Head>
            <Table.Body className="divide-y">
              {Array.from({ length: 6 }).map((_, index) => (
                <SkeletonRow key={index} />
              ))}
            </Table.Body>
          </Table>
        </ScrollableTableWrapper>
      ) : !hasData ? (
        <div className="flex w-full flex-col items-center justify-center py-8 text-gray-500">
          <svg
            className="mb-2 h-12 w-12 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-base font-medium">No churned accounts found</p>
          <p className="mt-1 text-sm">Try adjusting the time filter above</p>
        </div>
      ) : (
        <>
          <ScrollableTableWrapper height="calc(100vh - 320px)">
            <Table striped hoverable className="border-b drop-shadow-none">
              <Table.Head>
                {columns.map((column, index) => (
                  <Table.HeadCell
                    key={column}
                    className={
                      index === 0
                        ? "sticky left-0 z-20 bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                        : ""
                    }
                  >
                    {column}
                  </Table.HeadCell>
                ))}
              </Table.Head>
              <Table.Body className="divide-y">
                {churnedAccounts.map(
                  (
                    {
                      account_id,
                      logo,
                      account_name,
                      csm,
                      contract_end_date,
                      mrr,
                      churn_reason,
                      customer_lifespan_days,
                      cltv,
                      segment,
                      country,
                    },
                    rowIndex,
                  ) => {
                    const rowBgColor =
                      rowIndex % 2 === 0 ? "bg-white" : "bg-gray-50";
                    return (
                      <Table.Row key={account_id}>
                        <Table.Cell
                          className={`sticky left-0 z-10 w-40 ${rowBgColor} shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`}
                        >
                          <Link
                            className="space-y-1 font-bold hover:text-blue-500 hover:underline"
                            to={`/customers/${account_id}`}
                          >
                            <Avatar
                              size="xs"
                              img={logo}
                              className="flex justify-start"
                            >
                              {account_name}
                            </Avatar>
                          </Link>
                        </Table.Cell>
                        <Table.Cell>{csm}</Table.Cell>
                        <Table.Cell>
                          {new Date(contract_end_date).toDateString()}
                        </Table.Cell>
                        <Table.Cell className="font-bold">
                          {getFormattedMoney(mrr)}
                        </Table.Cell>
                        <Table.Cell>{churn_reason}</Table.Cell>
                        <Table.Cell>
                          {formatLifespan(customer_lifespan_days)}
                        </Table.Cell>
                        <Table.Cell>{getFormattedMoney(cltv)}</Table.Cell>
                        <Table.Cell>{segment}</Table.Cell>
                        <Table.Cell>{country}</Table.Cell>
                      </Table.Row>
                    );
                  },
                )}
              </Table.Body>
            </Table>
          </ScrollableTableWrapper>
          <div className="flex w-full items-center justify-between bg-white px-5 py-4 dark:bg-gray-900">
            <PaginationInfo page={page} pageSize={pageSize} total={total} />
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={onPageChange}
                showIcons
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};
